import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { X, PaperPlaneTilt, Copy } from "@phosphor-icons/react";

/**
 * Modal to invite a parent by email for one or more players.
 * props:
 *  - open, onClose
 *  - playerIds: string[]   (players the invited parent will be linked to)
 *  - defaultEmail, defaultName (optional prefills)
 */
export default function InviteParentModal({ open, onClose, playerIds = [], defaultEmail = "", defaultName = "" }) {
  const [email, setEmail] = useState(defaultEmail);
  const [name, setName] = useState(defaultName);
  const [password, setPassword] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  React.useEffect(() => {
    if (open) {
      setEmail(defaultEmail || "");
      setName(defaultName || "");
      setPassword("");
      setResult(null);
    }
  }, [open, defaultEmail, defaultName]);

  if (!open) return null;

  async function submit(e) {
    e.preventDefault();
    if (!playerIds.length) { toast.error("No player selected"); return; }
    setSending(true);
    try {
      const r = await api.post("/parents/invite", {
        email,
        name,
        player_ids: playerIds,
        password: password || undefined,
        login_url: `${window.location.origin}/login`,
      });
      setResult(r.data);
      if (r.data.email_sent) toast.success(`Invitation emailed to ${email}`);
      else toast.error("Parent created but the invite email failed. Share the login manually.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  }

  function copy(text) {
    navigator.clipboard.writeText(text).then(() => toast.success("Copied"));
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#121212] border border-neutral-800 rounded-lg p-6 max-w-md w-full space-y-4" onClick={(e) => e.stopPropagation()} data-testid="invite-parent-modal">
        <div className="flex items-start justify-between">
          <div>
            <div className="overline">Send Login Link</div>
            <div className="font-display font-black text-xl uppercase mt-1">Invite Parent</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-neutral-900" data-testid="invite-close-btn"><X size={20} weight="bold" /></button>
        </div>

        {!result ? (
          <form onSubmit={submit} className="space-y-3">
            <label className="block">
              <div className="text-xs text-neutral-500 font-semibold mb-1">Parent Name</div>
              <input required value={name} onChange={(e) => setName(e.target.value)} data-testid="invite-name" className="w-full h-12 px-3 bg-[#0a0a0a] border border-neutral-800 rounded-md focus:outline-none focus:border-[#D90429]" />
            </label>
            <label className="block">
              <div className="text-xs text-neutral-500 font-semibold mb-1">Parent Email</div>
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email" placeholder="parent@example.com" className="w-full h-12 px-3 bg-[#0a0a0a] border border-neutral-800 rounded-md focus:outline-none focus:border-[#D90429]" />
            </label>
            <label className="block">
              <div className="text-xs text-neutral-500 font-semibold mb-1">Password (optional — leave blank to auto-generate)</div>
              <input value={password} onChange={(e) => setPassword(e.target.value)} data-testid="invite-password" placeholder="Auto-generated if empty" className="w-full h-12 px-3 bg-[#0a0a0a] border border-neutral-800 rounded-md focus:outline-none focus:border-[#D90429]" />
            </label>
            <div className="text-xs text-neutral-500">Linking to {playerIds.length} player{playerIds.length === 1 ? "" : "s"}. If an existing parent has this email, the new player(s) will be added to their account.</div>
            <button type="submit" disabled={sending} data-testid="invite-send-btn" className="w-full h-12 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white font-display font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-60">
              <PaperPlaneTilt size={18} weight="bold" /> {sending ? "Sending…" : "Send Invite"}
            </button>
          </form>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-neutral-300">
              {result.is_new ? "Parent account created." : "Existing parent — added this player to their account."}
              {result.email_sent ? " Invitation email delivered." : " Email failed — share credentials manually below."}
            </div>
            {result.generated_password && (
              <div className="bg-[#0a0a0a] border border-neutral-800 rounded-md p-3">
                <div className="overline mb-1">Temporary Password</div>
                <div className="flex items-center justify-between gap-2">
                  <code className="text-sm font-mono text-[#FFD166]">{result.generated_password}</code>
                  <button onClick={() => copy(result.generated_password)} className="p-2 rounded-md border border-neutral-800 hover:border-[#06D6A0]"><Copy size={16} weight="bold" /></button>
                </div>
                <div className="text-xs text-neutral-500 mt-2">Share with the parent along with the login URL. Ask them to change it on first sign-in.</div>
              </div>
            )}
            <div className="bg-[#0a0a0a] border border-neutral-800 rounded-md p-3">
              <div className="overline mb-1">Login Link</div>
              <div className="flex items-center justify-between gap-2">
                <code className="text-xs font-mono text-neutral-300 break-all">{`${window.location.origin}/login`}</code>
                <button onClick={() => copy(`${window.location.origin}/login`)} className="p-2 rounded-md border border-neutral-800 hover:border-[#06D6A0] shrink-0"><Copy size={16} weight="bold" /></button>
              </div>
            </div>
            <button onClick={onClose} data-testid="invite-done-btn" className="w-full h-12 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white font-display font-bold uppercase tracking-wider">Done</button>
          </div>
        )}
      </div>
    </div>
  );
}
